import {renderAppointmentDetailsTable, renderConfirmationContainer, renderDetailRow} from "../utils/templates";
import {t} from "../i18n";
import {isNonNullObject, formatMoney, newClientRef} from "../utils/helpers.js";
import {stopHoldHeartbeat} from "../utils/slotHold.js";
import {showSlotTakenAlert, showServiceMissingAlert} from "../utils/alertDialog.js";
import {logClientEvent, describeError} from "../utils/jsErrorLogger.js";
import {authPayload, isReauthReason, takeResume} from "../auth/authState.js";
import {showBlockedNotice, BLOCKED_REASON} from "../utils/blockedNotice.js";

/**
 * Every Finalize failure must render SOMETHING — a 419/HTML/network error
 * showing nothing is exactly the iOS in-app-browser bug where the button
 * silently "did nothing" while the booking was lost. Optionally appends a
 * reload link (fresh session + fresh CSRF on the hosted/iframe page).
 */
function showBookingError($, container, message, withReload) {
  const $el = $('.field-error.field-generic-error', container);
  $el.text(message);
  if (withReload) {
    const $link = $('<a href="#" class="bw-reload-link"></a>').text(t('booking_reload_page'));
    $link.on('click', (e) => {
      e.preventDefault();
      window.location.reload();
    });
    $el.append(' ').append($link);
  }
  $el.show();
}

// Recuperarea după o cădere fără status: la prima întrebare serverul poate fi încă în mijlocul
// lui store() (fetch-ul cade în timp ce backend-ul lucrează), deci întrebăm de 4 ori, la 0, 2, 5
// și 10 s de la cădere (valorile sunt PAUZELE dintre întrebări). Butonul rămâne blocat între timp.
// De ce 10 s și nu 6 (27 sep 2026): la bormanstudio verificarea a renunțat și a arătat eroarea,
// iar programarea a apărut în baza de date la 7 s după — clientul a văzut „nu s-a putut”
// pentru o rezervare care tocmai se făcea. Cererile de verificare sunt citiri, deci încă una nu costă.
const RECOVERY_DELAYS_MS = [0, 2000, 3000, 5000];
const RECOVERY_TIMEOUT_MS = 6000;
const WARMUP_TIMEOUT_MS = 5000;

export default class ConfirmStep {
  constructor() {
    this.id = 'confirm';
    this.title_key = 'confirm_your_order'; // 'send_appointment' or 'confirm_your_order' or 'confirm_your_class'
  }

  init({ $, state, data, emit, api }) {
    this.$ = $;
    this.state = state;
    this.data = data;
    this.emit = emit;
    this.api = api;

    this.sub = null;
    this.retried419 = false;
    this.recovering = false;
    if(this.data.subscriptions && this.data.subscriptions.length > 0 && isNonNullObject(this.state.selectedSubscription)) {
      this.sub = this.state.selectedSubscription;
    }
    this.title_key = this.state.isClass ? 'confirm_your_class' : 'confirm_your_order';
  }

  /** The business's own client fields asked here (bwData.customFields), all mandatory. Not for a
   *  class: joinClass stores no request, so there is nothing to carry them to the client. */
  customFields() {
    if (this.state.isClass) {
      return [];
    }
    return Array.isArray(this.data.customFields) ? this.data.customFields : [];
  }

  /** What the fields start with: what was typed on this page before (bwState - survives going
   *  back and the re-auth round trip), else what this user gave this business last time. */
  customValues() {
    if (!this.state.customFieldValues || typeof this.state.customFieldValues !== 'object') {
      this.state.customFieldValues = {};
    }
    const saved = this.data.auth?.user?.custom_fields;
    return {...(saved && typeof saved === 'object' ? saved : {}), ...this.state.customFieldValues};
  }

  /** 422 from store() for these fields: `customFields.<key>` -> that row. Returns how many shown. */
  showCustomFieldErrors($html, errors) {
    let shown = 0;
    Object.keys(errors || {}).forEach((name) => {
      if (name.indexOf('customFields.') !== 0) {
        return;
      }
      const key = name.slice('customFields.'.length);
      const $field = $html.find('[data-cf-key]').filter((i, el) => this.$(el).attr('data-cf-key') === key);
      const msg = Array.isArray(errors[name]) ? errors[name][0] : errors[name];
      if ($field.length && msg) {
        $field.closest('.form-row').find('.field-cf-error').text(msg).show();
        shown++;
      }
    });
    return shown;
  }

  render(container) {
    const $html = this.$(
        renderConfirmationContainer(
            {
              config: this.data.config,
              isClass: this.state.isClass ?? false,
              customFields: this.customFields(),
              customValues: this.customValues()
            }
        )
    );
    const $sendBtn = this.$('#send-appointment', $html);
    container.html($html);

    $html.find('[data-cf-key]').on('input', (e) => {
      const $field = this.$(e.currentTarget);
      this.state.customFieldValues[$field.attr('data-cf-key')] = $field.val();
      $field.closest('.form-row').find('.field-cf-error').hide();
    });

    $sendBtn.on('click', e => {
      e.preventDefault();

      const btnStatus = $sendBtn.attr('disabled');
      console.log('btnStatus', btnStatus);
      if(btnStatus == 'disabled') {
        return; // button is disabled
      }

      // The business's fields: all mandatory. Checked here, before anything is sent; store()
      // checks again (and is the authority).
      const customFields = this.customFields();
      let customValues = null;
      if (customFields.length) {
        customValues = {};
        let $firstMissing = null;
        $html.find('[data-cf-key]').each((i, el) => {
          const $field = this.$(el);
          const key = $field.attr('data-cf-key');
          const value = String($field.val() ?? '').trim();
          if (value !== '') {
            customValues[key] = value;
            return;
          }
          const def = customFields.find((f) => f.key === key);
          $field.closest('.form-row').find('.field-cf-error')
              .text(t('custom_field_required', {field: def ? def.label : key})).show();
          if (!$firstMissing) {
            $firstMissing = $field;
          }
        });
        if ($firstMissing) {
          $firstMissing.trigger('focus');
          return;
        }
      }

      let $notesInput = $html.find('.confirmation-message #notes');
      // Din instanță, nu doar din global: this.data.auth e același obiect ca window.bwAuth, dar un al
      // doilea widget pe pagină suprascrie globalul.
      const auth = this.data.auth || window.bwAuth;
      const af = authPayload(auth);
      let dataToSend = {
        employeeId:    this.state.selectedEmployee,
        serviceId:     this.state.selectedServices,
        locationId:    this.state.selectedLocation,
        date:          this.state.date,
        startAt:       this.state.startAt,
        endAt:         this.state.endAt,
        // pending:       this.state.pending,
        // appointmentId: this.state.appointmentId,
        phone: af.phone,
        ...(af.phoneCountry ? {phone_country: af.phoneCountry} : {}),
        notes: $notesInput.length ? $notesInput.val() : '',
        noAccount: af.noAccount,
        isAuth: af.isAuth,
        // Prezența cheii = „bundle-ul ăsta știe să re-dovedească telefonul” (poarta din store());
        // 1 = retrimiterea de după re-auth.
        bwReauth: af.bwReauth
      };
      // reschedule
      if(this.state.appointmentId) {
        dataToSend.pending       = this.state.pending;
        dataToSend.appointmentId = this.state.appointmentId;
      }

      if(this.state.isClass) {
        dataToSend.isClass = true;
        dataToSend.classId = this.state.selectedClass;
      }
      if(this.state.selectedSubscription) {
        dataToSend.subscriptionId = this.state.selectedSubscription.id;
      }
      if(this.state.bookingName) {
        dataToSend.bookingName = this.state.bookingName;
      }
      // Only when this booking asks them: the key's presence is what makes store() check them.
      if (customValues) {
        dataToSend.customFields = customValues;
      }
      // Marker for ops telemetry: 0 = first attempt, 1 = the automatic re-send
      // after a 419. Lets the server-side 419 alert say "first try — the retry
      // is probably rescuing this right now" vs "failed EVEN AFTER retry".
      // In the BODY, not a header: a custom header would force a CORS preflight
      // change for embedded widgets; store() simply ignores unknown fields.
      dataToSend.bwRetry = this.retried419 ? 1 : 0;
      // Acelasi cod pentru toate incercarile aceleiasi rezervari (inclusiv un al doilea click dupa o
      // eroare), ca si garda anti-dublura din store() sa recunoasca prima cerere chiar de pe alt IP.
      // Se schimba doar dupa un succes (onStored), pentru rezervarea urmatoare din aceeasi pagina.
      // Acelasi cod cu care s-a tinut ora la alegere (state.clientRef): serverul recunoaste tinerea
      // ca fiind a ei si nu o refuza din cauza propriei tineri.
      if (!this.clientRef) this.clientRef = this.state.clientRef || newClientRef();
      this.state.clientRef = this.clientRef;
      if (this.clientRef) dataToSend.clientRef = this.clientRef;
      $sendBtn.attr('disabled', 'disabled');
      $sendBtn.addClass('anim-spinner');
      $(`.field-error`, container).hide();

      const releaseBtn = (delayMs = 0) => {
        $sendBtn.removeClass('anim-spinner');
        if (delayMs) {
          setTimeout(() => $sendBtn.removeAttr('disabled'), delayMs);
        } else {
          $sendBtn.removeAttr('disabled');
        }
      };

      // Răspunsul de succes al lui store() — sau al verificării „a ajuns rezervarea?” de mai jos,
      // care răspunde în exact aceeași formă.
      const onStored = (data) => {
        this.clientRef = null; // rezervarea urmatoare din pagina primeste un cod nou
        this.state.clientRef = null;
        stopHoldHeartbeat(); // serverul a sters deja tinerea
        if(data?.requires_validation) {
          // The row was stored but NOT validated/notified — the salon never
          // sees it. Showing the thanks screen here silently loses the
          // booking; be honest instead (reload → fresh session → the SMS
          // validation flow runs again on retry).
          showBookingError(this.$, container, t('booking_needs_validation'), true);
          logClientEvent({message: 'storeAppointment requires_validation', step: 'confirm'});
          releaseBtn();
          return;
        }
        this.state.appointmentLink = data.appLink ?? '';
        // Truthy check: backend always includes `redirect_url` in the response
        // (defaults to '' when `account.booking_success_redirect` is not set),
        // so hasOwnProperty was matching the empty-string case and skipping
        // the thanks page. Empty string / null / undefined → fall to thanks.
        if(!this.sub && data && data.redirect_url) {
          this.emit('finish_with_redirect', data.redirect_url);
        } else {
          // if(this.data.config.use_deposit) { console.log('No redirect.'); }
          this.emit('finish_with_thanks', data);
        }
        releaseBtn();
      };

      // Eroarea vizibilă + telemetria. `lookupErr` = ce a răspuns verificarea „a ajuns
      // rezervarea?”, ca mailul să spună dacă recuperarea a fost încercată și de ce a ratat.
      const showFailure = (err, lookupErr = null) => {
        const status = err && err.status;
        let message = null;
        let withReload = false;
        let slotTaken = false;
        let serviceMissing = false;
        if (status === 419) {
          // Session/CSRF gone — webview dropped or expired the cookie.
          message = t('booking_session_expired');
          withReload = true;
        } else if (err && err.data && err.data.error) {
          // Custom JSON error from our own endpoints.
          message = err.data.error;
        } else if (err && err.data && (err.data.errors || status === 422)) {
          // Laravel validation shape: {message, errors: {field: [msg]}} —
          // e.g. the localized slot_just_taken conflict (422). Only for
          // validation: a 500/429 also carries {message: 'Server Error'} in
          // English, which must not reach the client untranslated.
          const errors = err.data.errors || {};
          if (status === 422 && this.showCustomFieldErrors($html, errors) > 0) {
            // Shown on their own rows; nothing for the generic line.
            releaseBtn();
            return;
          }
          const firstKey = Object.keys(errors)[0];
          const first = firstKey ? errors[firstKey] : null;
          message = (Array.isArray(first) ? first[0] : first) || err.data.message || t('booking_error_generic');
          slotTaken = status === 422 && firstKey === 'startAt';
          serviceMissing = status === 422 && firstKey === 'serviceId';
        } else if (!status) {
          // fetch rejected outright — offline / blocked by the webview / connection
          // closed under the request (and the server-side lookup found nothing).
          message = t('booking_network_error');
        } else {
          message = t('booking_error_generic');
        }
        showBookingError(this.$, container, message, withReload);
        if (slotTaken) {
          // Ora s-a ocupat intre timp (verificarea de la Finalize): alerta cu „Am inteles”, apoi inapoi la
          // lista de ore, reincarcata - restul datelor ei raman completate (app.js, 'slot_taken').
          showSlotTakenAlert(message, this.$(container).closest('.booking-widget')[0])
            .then(() => this.emit('slot_taken'));
        }
        if (serviceMissing) {
          // Nimic ales la servicii (reflexovital 29 oct: login dublu sarise pasul): alerta, apoi inapoi
          // la servicii (app.js, 'services_missing').
          showServiceMissingAlert(message, this.$(container).closest('.booking-widget')[0])
            .then(() => this.emit('services_missing'));
        }
        const lookupNote = lookupErr
            ? ` | recent=${lookupErr.status ?? 'network'}${lookupErr.data?.reason ? ':' + lookupErr.data.reason : ''}`
            : '';
        logClientEvent({
          message: 'storeAppointment failed',
          status: status ?? null,
          step: 'confirm',
          // numele erorii + online/hidden la cădere de rețea; corpul (+ eroarea de parsare) altfel
          context: describeError(err) + lookupNote
        });
        releaseBtn(5000);
      };

      const onError = (err) => {
        console.warn(err);
        const status = err && err.status;
        const reason = (err && err.data && err.data.reason) || null;

        // Blocat la programari online (2.0.7): cine n-a trecut prin pasul cu telefonul (logat, telefon
        // verificat) afla aici - acelasi ecran: rezumatul si butonul inlocuite de mesaj. Fara mail de
        // alarma: e raspunsul cerut de salon, nu o cadere.
        if (status === 403 && reason === BLOCKED_REASON) {
          showBlockedNotice(this.$, container, container.children(), err.data && err.data.error);
          return;
        }

        // RE-AUTH: 419 {reason} = sesiunea a murit și serverul n-are dovadă pentru telefon. Retry-ul
        // CSRF de mai jos ar retrimite același payload → al doilea 419 și rezervarea pierdută
        // (dollieskin, 2026-09-12). Cerem codul SMS o singură dată pe pagină, păstrând slotul și
        // notițele; confirm-ul nou retrimite singur dacă slotul a rămas același.
        if (isReauthReason(err)) {
          if (this.data.auth && !this.data.auth.reauthUsed) {
            logClientEvent({
              message: 'storeAppointment reauth required',
              status: 419,
              step: 'confirm',
              context: `reason=${reason} | phone=${dataToSend.phone ? 1 : 0} | resched=${dataToSend.appointmentId ? 1 : 0} | sub=${dataToSend.subscriptionId ? 1 : 0} | class=${dataToSend.isClass ? 1 : 0}`
            });
            releaseBtn();
            this.emit('reauth_required', {notes: dataToSend.notes, reason});
            return;
          }
          // Refuzat din nou DUPĂ un re-auth: mesajul de sesiune expirată + reîncărcare, iar
          // 'storeAppointment failed' tot pleacă pe mail. Fără a doua buclă de SMS.
          showFailure(err);
          return;
        }

        // SELF-HEAL: a 419 on a long-lived tab just means the CSRF/session
        // went stale. Fetch a fresh token (the GET also mints/extends the
        // session) and re-submit ONCE, silently — most clients never see an
        // error at all. A second 419 falls through to the visible message.
        // Only a GENUINE CSRF 419 (TokenMismatch, body without `reason`): a
        // reason-tagged 419 is handled above and must never be blindly re-sent.
        if (status === 419 && !reason && !this.retried419 && this.data.apiRoutes.csrfRefresh) {
          this.retried419 = true;
          logClientEvent({message: 'storeAppointment 419 auto-retry', status: 419, step: 'confirm'});
          this.api.get(this.data.apiRoutes.csrfRefresh, {
            timeoutMs: WARMUP_TIMEOUT_MS,
            success: (r) => {
              if (r && r.csrf) {
                this.api.setCSRF(r.csrf);
              }
              releaseBtn();
              $sendBtn.trigger('click');
            },
            error: () => {
              showBookingError(this.$, container, t('booking_session_expired'), true);
              releaseBtn(5000);
            }
          });
          return; // keep quiet — the retry is in flight
        }

        // RECUPERARE: fetch-ul a picat FĂRĂ status. Pe 2026-09-10 asta însemna, de trei ori în
        // 24 h, că load balancer-ul închisese conexiunea inactivă chiar când pleca POST-ul:
        // serverul a salvat rândul (200 complet în access log), răspunsul n-a mai ajuns, iar
        // browserul nu reia niciodată un POST. „Verifică conexiunea” i-a făcut pe oameni să
        // refacă programarea (breathless: dublură pe același slot; alexis: a doua programare la
        // altă oră). Întrebăm serverul (același angajat, slot, telefon, servicii, IP, ultimele
        // 15 min) de până la 3 ori — la prima întrebare poate fi încă în mijlocul lui store() —
        // și, dacă rezervarea există, intrăm pe drumul normal de succes. Abia apoi eroarea.
        if (!status && this.data.apiRoutes.appointmentRecent && !this.recovering) {
          this.recovering = true;
          const lookupData = {
            employeeId: dataToSend.employeeId,
            serviceId:  dataToSend.serviceId,
            date:       dataToSend.date,
            startAt:    dataToSend.startAt,
            ...(dataToSend.phone ? {phone: dataToSend.phone} : {}),
            ...(dataToSend.phone_country ? {phone_country: dataToSend.phone_country} : {}),
            ...(dataToSend.clientRef ? {clientRef: dataToSend.clientRef} : {})
          };
          const attempt = (i) => {
            this.api.get(this.data.apiRoutes.appointmentRecent, {
              data: lookupData,
              timeoutMs: RECOVERY_TIMEOUT_MS,
              success: (found) => {
                this.recovering = false;
                logClientEvent({message: 'storeAppointment recovered after network failure', status: 200, step: 'confirm', context: `attempt=${i + 1} | ` + describeError(err)});
                onStored(found);
              },
              error: (lookupErr) => {
                // „încă nu” (404 fără motiv) sau rețea → mai încercăm; 404:deposit / 422 / 429 → nu are rost
                const retryable = !lookupErr?.status || (lookupErr.status === 404 && !lookupErr?.data?.reason);
                if (retryable && i + 1 < RECOVERY_DELAYS_MS.length) {
                  setTimeout(() => attempt(i + 1), RECOVERY_DELAYS_MS[i + 1]);
                  return;
                }
                this.recovering = false;
                showFailure(err, lookupErr);
              }
            });
          };
          setTimeout(() => attempt(0), RECOVERY_DELAYS_MS[0]);
          return;
        }

        showFailure(err);
      };

      const send = () => this.api.post(this.data.apiRoutes.storeAppointment, {
        data: dataToSend,
        success: onStored,
        error: onError
      });

      // ÎNCĂLZIRE: un GET mic chiar înainte de POST. Load balancer-ul închide conexiunile
      // inactive (~60 s), exact cât stă omul pe formularul de confirmare, iar browserul nu reia
      // un POST plecat pe o conexiune tocmai închisă. Un GET (pe care browserul îl reia singur
      // la nevoie) lasă conexiunea proaspătă pentru POST și, bonus, aduce un token CSRF nou.
      // Dacă GET-ul pică sau depășește timpul, trimitem oricum: recuperarea de mai sus rămâne
      // plasa de siguranță.
      if (this.data.apiRoutes.csrfRefresh) {
        this.api.get(this.data.apiRoutes.csrfRefresh, {
          timeoutMs: WARMUP_TIMEOUT_MS,
          success: (r) => {
            if (r && r.csrf) {
              this.api.setCSRF(r.csrf);
            }
            send();
          },
          error: send
        });
      } else {
        send();
      }
    })

    // Revenire după re-auth-ul de la Finalize: notițele tastate înainte de 419 se pun la loc, iar
    // dacă slotul e neschimbat retrimitem singuri (omul apăsase deja Finalize). Instanța e nouă, deci
    // retried419 pornește de la false. Termenii sunt text simplu, fără bifă de consimțământ sărită.
    const resume = takeResume(this.state);
    if (resume) {
      const $notes = $html.find('.confirmation-message #notes');
      if (resume.notes && $notes.length && !$notes.val()) {
        $notes.val(resume.notes);
      }
      if (resume.submit) {
        setTimeout(() => $sendBtn.trigger('click'), 0);
      }
    }

    this.api.get(this.data.apiRoutes.getAppointmentSummary, {
      data: {
        data: {
          selectedClass: this.state.selectedClass ?? null,
          selectedEmployeeId: this.state.selectedEmployee,
          selectedServiceId: [...this.state.selectedServices],
        }
      },
      success: (data) => {
        this.state.appointmentSummaryData = data;

        const $detailsTable = $html.find('.confirmation-details-table');
        renderAppointmentDetailsTable({
          table: $detailsTable,
          state: this.state
        });

        if (!!this.sub) {
          $detailsTable.append(renderDetailRow('subscription.confirm_label', this.sub.title));
        }

        if (!this.sub && this.data.config.use_deposit && data.depositValue) {
          // depositCurrency comes from getAppointmentSummary, which reads the same
          // Account::currency accessor as bwData.account.currency. Fall back to the
          // bootstrap value so a summary response that predates that change can
          // never print "50 undefined" next to correctly-labelled service lines.
          const depositCurrency = data.depositCurrency || this.data.account?.currency;
          $detailsTable.append(renderDetailRow('deposit_required', formatMoney(data.depositValue, depositCurrency)));
          $sendBtn.first().text(t('finish_with_deposit'));
        } else {
          $sendBtn.first().text(t('finish'));
        }
      },
      error: (err) => {
        console.warn(err);
      }
    })
  }
};
